import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogContent,
  Paper,
  DialogActions,
  TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from './styles';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { PreFlashRecord } from '../../../store/bulk-device-flash/types';
import { useMountEffect } from '../../../globals/hooks/use-mount-effect';
import { softwareHeadCells, softwareHeadCellsAccessors } from './head-cells';
import VirtualizedTable from '../../virtualized-table';
import {
  queueAndFlashSoftware,
  uploadSoftwareConfigFile,
  uploadSoftwareConfigExistingFile,
} from '../../../store/bulk-device-flash/actions';
import { AppState } from '../../../store';
import FileUploader from '../../file-loader';
import CancelIcon from '../../../assets/icons/cancel-icon';
import { setModalOpen } from '../../../store/modal/actions';
import { softwareConfigFileAlreadyExistsWillNotShowInLibraryMessage } from '../../configure/messages';
import { RemoteFlashActionNames } from '../../../store/configure/remote-flash/types';

const fileTypeOptions: { [key: string]: { value: number; label: string; tag: string } } = {
  '10330': { value: 10330, label: 'Single Config File', tag: 'singleconfigfile' },
  '10001': {
    value: 10001,
    label: 'Standard Parameter Config file',
    tag: 'standardparameterconfigfile',
  },
  '10544': {
    value: 10544,
    label: 'Phoenix Radio Parameter Config File',
    tag: 'phoenixRadioParameterConfigFile',
  },
};

const QueueFiles = ({ rows, closePopover }: { rows: PreFlashRecord[]; closePopover: Function }) => {
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const configFilesLibrary = new ConfigurationFilesLibrary(
    client,
    envVariables.configurationFilesLibraryV1BaseUrl,
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const fileExistsInDatabase = useSelector(
    (state: AppState) => state.firmware.softwareFileExistsInDb,
  );
  const hashExistsInDatabase = useSelector((state: AppState) => state.firmware.hashExistsInDb);
  const [softwareToFlash, setSoftwareToFlash] = useState<{
    fileTypeId: string;
    lastUpdatedBySystem: string;
    id: string;
    partNumber: string;
  }>();
  const [tab, setTab] = useState<number>(1);
  const [softwareTableData, setSoftwareTableData] = useState<any[] | [] | null>(null);
  const [configFilesTableData, setConfigFilesTableData] = useState<any[] | [] | null>(null);
  const [queueConfigFilesData, setQueueConfigFilesData] = useState<any>({});
  const [selectedItem, setSelectedItem] = useState<number | null>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [popoverOpen, setPopoverOpen] = useState<boolean>(true);
  const [fileTypeId, setFileTypeId] = useState<number>(0);
  const [percentUpload, setPercentUpload] = useState<number>(0);
  const [fileToPush, setFileToPush] = useState<any>();
  const [partNumber, setPartNumber] = useState<string>('');
  const tableContainer = useRef<any>();

  const resetSelection = () => {
    setSoftwareToFlash(undefined);
    setSelectedItem(null);
    setFileToPush(null);
  };

  const Checkbox = ({ row }: any) => (
    <>
      <input
        type="checkbox"
        checked={row.id === selectedItem}
        onChange={() => {
          resetSelection();
          setSelectedItem(row.id);
          setSoftwareToFlash({
            fileTypeId: row.original.fileTypeId,
            lastUpdatedBySystem: row.original.lastUpdatedBySystem,
            id: row.original.id,
            partNumber,
          });
        }}
      />
    </>
  );

  const fileHandler = (file: any) => {
    resetSelection();
    setFileToPush(file);
  };

  const getSoftwareFiles = async () => {
    setIsLoading(true);
    if (!softwareTableData && fileTypeId > 0) setSoftwareTableData([]);
    const softwareResponse = await configFilesLibrary.getConfigFiles({
      fileTypeId: `${`10030`}`,
      isRetrievable: true,
    });
    if (softwareResponse?.data?.configs) {
      setSoftwareTableData(softwareResponse.data.configs);
    } else {
      setSoftwareTableData(null);
    }
    setIsLoading(false);
  };

  const getConfigurationFiles = async () => {
    setIsLoading(true);
    if (!configFilesTableData) {
      const newTableData = [];
      try {
        const configFilesResponse1 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10001`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse1?.data?.configs || []))
      } catch {
        newTableData.push(...[]);
      }
      try {
        const configFilesResponse2 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10330`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse2?.data?.configs || []))
      } catch {
        newTableData.push(...[]);
      }
      try {
        const configFilesResponse3 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10544`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse3?.data?.configs || []))
      } catch {
        newTableData.push(...[]);
      }
      setConfigFilesTableData(newTableData);
    }
    setIsLoading(false);
  };

  const handleConfigFileType = async (event: any) => {
    setIsLoading(true);
    setFileTypeId(event.target.value);
    const newTableData = [];
    if (event.target.value === 10001) {
      try {
        const configFilesResponse1 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10001`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse1?.data?.configs || []))
      } catch {
        newTableData.push(...[]);
      }
    } else if (event.target.value === 10330) {
      try {
        const configFilesResponse2 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10330`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse2?.data?.configs || []))
      } catch {
        newTableData.push(...[]);
      }
    } else if (event.target.value === 10544) {
      try {
        const configFilesResponse3 = await configFilesLibrary.getConfigFiles({
          fileTypeId: `${fileTypeOptions[`10544`].value}`,
          isRetrievable: true,
        });
        newTableData.push(...(configFilesResponse3?.data?.configs || []));
      } catch {
        newTableData.push(...[]);
      }
    }
    else {
      await getConfigurationFiles();
    }
    setConfigFilesTableData(newTableData);
    setIsLoading(false);
  }

  useMountEffect(() => {
    getSoftwareFiles();
  });

  const flashDevices = async () => {
    if (softwareToFlash && selectedItem) {
      closePopover();
      dispatch(
        queueAndFlashSoftware({
          devicesToQueue: rows,
          softwareToQueue: softwareToFlash,
        }),
      );
    } else if (fileToPush) {
      const data = {
        devicesToQueue: rows,
        file: fileToPush,
        fileTypeId: fileTypeId ? `${fileTypeId}` : '10030',
        tag: fileTypeId ? `${fileTypeOptions[fileTypeId].tag}` : 'deploymentfile',
        partNumber,
        setPercentUpload,
        finishedCallback: () => { },
        onSuccessCallback: () => { closePopover() },
        overwrite: false,
      };
      setQueueConfigFilesData(data);
      dispatch(uploadSoftwareConfigFile(data));
    }
  };

  const handleSoftwareFileOverwrite = (dispatch: any) => {
    const replaceFileData = { ...queueConfigFilesData, overwrite: false, hash: hashExistsInDatabase };
    dispatch(
      setModalOpen({
        open: true,
        message: softwareConfigFileAlreadyExistsWillNotShowInLibraryMessage,
        accept: () => {
          dispatch(uploadSoftwareConfigExistingFile(replaceFileData));
          closePopover();
          setFileToPush(null);
        },
        cancel: () => {
          dispatch({
            type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
            payload: false,
          });
        },
      }),
    );
  };

  useEffect(() => {
    if (fileExistsInDatabase && hashExistsInDatabase) {
      handleSoftwareFileOverwrite(dispatch);
    }
  }, [fileExistsInDatabase, hashExistsInDatabase, dispatch]);

  const checkColumn = {
    accessor: 'checkbox',
    width: 30,
    Header: '',

    component: Checkbox,
  };
  const firmwareData = useMemo(() => softwareTableData, [softwareTableData]);
  const configFilesData = useMemo(() => configFilesTableData, [configFilesTableData]);
  const columns = useMemo(() => [checkColumn, ...softwareHeadCells], [checkColumn]);
  const data = tab === 1 ? firmwareData : configFilesData;

  const handleSetTab = (newValue: number) => {
    resetSelection();
    if (newValue === 2 && !configFilesTableData) getConfigurationFiles();
    setTab(newValue);
  };

  const handlePartNumber = (event: any) => {
    setPartNumber(event.target.value);
  };

  const handleClick = () => {
    setPopoverOpen(true);
  };

  return (
    <Dialog open={popoverOpen} PaperComponent={Paper} className={classes.dialog} maxWidth="lg">
      <Paper>
        <DialogContent>
          <Box p={2} onClick={handleClick}>
            <Box pb={2}>
              <Typography variant="h5">Queue Files</Typography>
            </Box>
            <Divider />
            <Box
              p={1}
              pb={2}
              flexGrow={0}
              className={classes.noWrap}
              justifyContent="space-between"
              alignItems="center"
            >
              <Box pb={2} px={1} display="flex" justifyContent="space-between">
                <Tabs
                  className={classes.tabs}
                  value={tab}
                  onChange={(event, newValue) => handleSetTab(newValue)}
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  <Tab value={1} label="Software" />
                  <Tab value={2} label="Configuration Files" />
                </Tabs>
              </Box>
              <Box
                p={1}
                pb={2}
                flexGrow={0}
                className={classes.noWrap}
                justifyContent="space-between"
                alignItems="center"
              >
                {!percentUpload ? (
                  <>
                    <Box
                      p={1}
                      pb={3}
                      className={classes.noWrap}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        {tab === 1 && (
                          <>
                            <Box p={1} pr={0}>
                              <Typography variant="h6">Part Number:</Typography>
                            </Box>
                            <Box p={1} mt="-16px">
                              <TextField
                                variant="standard"
                                className={classes.partNumber}
                                margin="none"
                                id="part-number-input"
                                label="Part#"
                                onChange={handlePartNumber}
                                required
                              />
                            </Box>
                          </>
                        )}
                        <Box p={1} pr={0}>
                          <Typography variant="h6">Upload a file to Queue</Typography>
                        </Box>
                        <Box p={1}>
                          <FileUploader handleFile={fileHandler} />
                        </Box>
                        <Box p={1}>
                          <Typography>File Type: </Typography>
                        </Box>
                        {tab === 2 ? (
                          <FormControl>
                            <Select
                              variant="standard"
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={fileTypeId}
                              onChange={handleConfigFileType}
                            >
                              <MenuItem value={0}>
                                <em className={classes.defaultSelect}>Select</em>
                              </MenuItem>
                              {Object.keys(fileTypeOptions).map((key: string) => (
                                <MenuItem value={fileTypeOptions[key].value}>
                                  {fileTypeOptions[key].label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography>Deployment file</Typography>
                        )}
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box p={1} display="flex" flexGrow={1} width="100%" alignItems="center">
                      <Typography>progress</Typography>
                      <Box p={2} width="100%">
                        <LinearProgress
                          className={classes.progress}
                          variant="determinate"
                          color="secondary"
                          value={percentUpload}
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {softwareTableData && (
                  <div ref={tableContainer} className={classes.tableContainer}>
                    <VirtualizedTable
                      sortableAccessors={softwareHeadCellsAccessors}
                      columns={columns}
                      data={data || []}
                      itemCount={data?.length || 0}
                      tableContainer={tableContainer}
                      loading={isLoading}
                    />
                  </div>
                )}
                <Box
                  display="flex"
                  flexGrow={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  pr={2}
                >
                  {(fileToPush || selectedItem) && (
                    <>
                      <Box px={1}>
                        <Typography variant="h6">
                          {fileToPush
                            ? `File Name:`
                            : `Selected ${tab === 1 ? `Software:` : `Configuration File:`}`}
                        </Typography>
                      </Box>
                      <Box px={1} flexGrow={0} overflow="hidden">
                        <Typography className={classes.overFlowEllipsis}>
                          {(selectedItem && data && data[selectedItem].fileName) || fileToPush.name}
                        </Typography>
                      </Box>
                      <Box px={1} pr={2}>
                        <IconButton
                          className={classes.iconButton}
                          id="queue-files-delete-selection"
                          onClick={() => resetSelection()}
                          aria-label="edit"
                          size="large"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                  <DialogActions>
                    <Button
                      onClick={() => closePopover()}
                      disabled={false}
                      variant="outlined"
                      size="small"
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={flashDevices}
                      disabled={
                        !(
                          selectedItem ||
                          (fileToPush && fileTypeId && partNumber) ||
                          (fileToPush && partNumber && tab === 1) ||
                          (fileToPush && !partNumber && tab === 2)
                        )
                      }
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon
                    >
                      Queue and Flash
                    </Button>
                  </DialogActions>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Paper>
    </Dialog>
  );
};

export default QueueFiles;
